import React, {useRef} from 'react';
import './tournament.css';
import {canJoinTournament} from '@utils/game.util';
import {useAuth} from '@hooks/use-auth';
import CompetitionEventImg from '@assets/images/thumbnails/event-card-thumbnail-1.png';
import {Tournament} from 'interfaces/tournament.interface';
import {formatAmount} from '@utils/helpers';
import Spinner from '@components/ui/spinner';
import {useTranslation} from 'react-i18next';
import {MODAL_TYPES, useModal} from '@contexts/global-modal-context';
import {useTournament} from '@hooks/use-tournament';
import moment from 'moment';
import {getRandomAvatar} from '@constants/avatars';
import {toPng} from 'html-to-image';
import {Alert} from '@utils/alert';

// Notice here we're using the function declaration with the interface Props
export interface TournamentWidgetProps {
    tournament: Tournament;
    isPendingPart?: boolean;
    onSelectTournament?: (tournament: Tournament) => void;
}

const TournamentWidget: React.FunctionComponent<TournamentWidgetProps> = ({
                                                                              tournament,
                                                                              isPendingPart,
                                                                              onSelectTournament,
                                                                          }: TournamentWidgetProps) => {
    const widgetRef = useRef(null);
    const {user} = useAuth();
    const {t} = useTranslation(['competition']);
    const {showModal} = useModal();
    const {
        joinTournament,
        leaveTournament,
        showDetails,
        isJoinning,
        isLeaving,
        isShowing,
        canLeaveTournament
    } = useTournament();
    const subscribers = Array.from({length: Math.min(4, tournament.nb_users || 0)}, (_, i) => i + 1);
    const getSpecialClass = () => {
        let clazz = 'is-competition';
        if (isPendingPart && !canJoinTournament(tournament, user?.balance ?? 0)) {
            clazz = 'cannot-join';
        }
        return clazz;
    };

    const handleJoinTournament = (e: React.MouseEvent) => {
        stopPropagation(e);

        if (tournament.register_fees > 0) {
            Alert.confirm(
                t('confirm_label', {ns: 'alert'}),
                t('join_tournament_confirmation', {amount: tournament.register_fees}),
                [
                    {
                        text: t('cancel', {ns: 'actions'}),
                        onPress: onCancel,
                    },
                    {
                        text: t('i_join', {ns: 'actions'}),
                        onPress: onJoinTournament,
                    },
                ],
                {
                    cancelable: true,
                },
            );
        } else {
            onJoinTournament();
        }
    };

    const onCancel = () => {
        //
    };
    const onJoinTournament = () => {
        joinTournament(tournament);
    };

    const onLeaveTournament = (e: React.MouseEvent) => {
        stopPropagation(e);
        if (tournament.cash_prize <= 0) {
            leaveTournament(tournament);
        }
    };

    const _showDetails = async () => {
        showDetails(tournament._id, async (_tournament) => {
            _tournament.nb_users = tournament.nb_users;
            _tournament.is_user_registered = tournament.is_user_registered;
            if (widgetRef.current) {
                toPng(widgetRef.current)
                    .then((dataUrl) => {
                        _tournament.picture = dataUrl;
                    })
                    .catch((error) => {
                        console.error('Error generating image:', error);
                    });
            }
            onSelectTournament?.(_tournament);
        });
    };

    const handleShare = (e: React.MouseEvent) => {
        stopPropagation(e);
        if (widgetRef.current) {
            toPng(widgetRef.current)
                .then((dataUrl) => {
                    const title = `${tournament.name} (Du ${moment(tournament.automation.rounds[0].start_at).format('D MMM YYYY [at] HH:mm [GMT]Z')} au ${moment(tournament.end_at).format('D MMM YYYY [at] HH:mm [GMT]Z')}), La mise est de ${tournament.register_fees} FCFA, le gagnant raffle le jackpot de ${tournament.cash_prize} FCFA`;
                    const url = window.location + '?tour=' + tournament._id;
                    showModal(MODAL_TYPES.SHARE_MODAL, {imageUrl: dataUrl, title, url});
                })
                .catch((error) => {
                    console.error('Error generating image:', error);
                });
        }
    };

    const stopPropagation = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    return (
        <div className={`event-card ${getSpecialClass}`} ref={widgetRef} onClick={_showDetails}>
            <div className="event-card-header">
                <div className="event-card-cover">
                    <img src={CompetitionEventImg} alt=""/>
                    <button
                        className="event-card-button event-share default position-absolute"
                        type="button"
                        onClick={handleShare}
                    >
                        <span className="icon icon16">
                            <svg viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M10.7342 9.89841L15.2202 6.13841C15.3973 5.99369 15.5 5.77709 15.5 5.54841C15.5 5.31972 15.3973 5.10313 15.2202 4.95841L10.7342 1.19841C10.495 0.993872 10.16 0.94373 9.87132 1.06924C9.58265 1.19475 9.39085 1.47393 9.37724 1.78841V3.27141C3.07924 2.18041 1.50024 7.81641 1.50024 10.9984C2.96124 8.57441 6.74724 4.18041 9.37724 7.81641V9.30341C9.38904 9.61879 9.58014 9.89966 9.86918 10.0264C10.1582 10.1531 10.4943 10.1034 10.7342 9.89841Z"
                                    stroke="white"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                ></path>
                            </svg>
                        </span>
                    </button>
                </div>
                <div className="event-card-globals">
                    <div className="even-fund">
                        <span className="icon">
                            <svg
                                width="13"
                                height="12"
                                viewBox="0 0 13 12"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M7.06755 6.40638C6.96882 6.40638 6.87212 6.37251 6.79432 6.30894C6.69397 6.22694 6.63574 6.10419 6.63574 5.9746V1.45717C6.63574 1.21871 6.82906 1.02539 7.06752 1.02539L11.2979 1.02539C11.5248 1.02539 11.7346 1.12832 11.8735 1.30783C12.0124 1.48731 12.0594 1.71626 12.0025 1.93596L11.5036 3.86067C11.2388 4.8823 10.4388 5.67804 9.41576 5.93737C9.40908 5.93906 9.40239 5.94058 9.39565 5.94195L7.15354 6.39773C7.12503 6.40354 7.0962 6.40638 7.06755 6.40638V6.40638ZM7.49935 1.88895V5.4462L9.21307 5.09783C9.92594 4.91349 10.4828 4.35724 10.6677 3.64398L11.1226 1.88898L7.49935 1.88898V1.88895Z"
                                    fill="#FFC250"
                                ></path>
                                <path
                                    d="M4.95862 6.40638C4.92996 6.40638 4.90118 6.40354 4.8726 6.39773L2.63049 5.94195C2.62377 5.94058 2.61708 5.93903 2.61043 5.93737C1.5874 5.67802 0.787375 4.8823 0.522579 3.86065L0.0236652 1.93596C-0.0332965 1.71628 0.0136822 1.48733 0.152598 1.30785C0.291491 1.12834 0.501321 1.02539 0.728276 1.02539L4.95862 1.02539C5.19708 1.02539 5.3904 1.21871 5.3904 1.45717V5.9746C5.3904 6.10419 5.33219 6.22694 5.23182 6.30894C5.15407 6.37251 5.05734 6.40638 4.95862 6.40638V6.40638ZM2.81312 5.09783L4.52686 5.4462V1.88895L0.9036 1.88895L1.35852 3.64396C1.54338 4.35724 2.10024 4.91347 2.81312 5.09783Z"
                                    fill="#FFC250"
                                ></path>
                                <path
                                    d="M7.49114 9.70062C6.37051 9.33071 5.58945 8.17851 5.54801 6.8527C5.59318 6.6432 5.61681 6.42658 5.61681 6.20569V4.74023H5.30186V6.20569C5.30186 7.57206 4.39785 8.77361 3.08496 9.15221V10.0401H7.76315V9.79042L7.49114 9.70062Z"
                                    fill="#FFD064"
                                ></path>
                                <path
                                    d="M8.6687 9.07378C7.51692 8.74164 6.72382 7.68754 6.72382 6.4888V4.74023H5.3022V6.20569C5.3022 6.4266 5.27857 6.6432 5.2334 6.8527C5.27483 8.17851 6.0559 9.33071 7.17653 9.70062L7.44856 9.79042V10.0401H8.94073V9.15221L8.6687 9.07378Z"
                                    fill="#FFC250"
                                ></path>
                                <path
                                    d="M8.09314 9.14258H2.57519C2.36 9.14258 2.18555 9.31701 2.18555 9.53222V11.4886H8.48276V9.53222C8.48279 9.31701 8.30833 9.14258 8.09314 9.14258Z"
                                    fill="#FFE07D"
                                ></path>
                                <path
                                    d="M9.44961 9.14258H7.89062C8.10581 9.14258 8.28027 9.31703 8.28027 9.53222V11.4886H9.83925V9.53222C9.83923 9.31703 9.6648 9.14258 9.44961 9.14258Z"
                                    fill="#FFD064"
                                ></path>
                                <path
                                    d="M7.97314 2.71583V0.529297L2.77148 0.529297V2.64017C2.77148 4.37192 3.70899 5.96798 5.22154 6.81124C5.33406 6.87399 5.45219 6.92237 5.57332 6.95646C7.05888 6.06449 7.97314 4.45632 7.97314 2.71583Z"
                                    fill="#FFE07D"
                                ></path>
                                <path
                                    d="M7.69383 0.529297V2.64017C7.69383 4.37192 6.75632 5.96798 5.24374 6.81124C5.23996 6.81333 5.2362 6.81542 5.23242 6.81749C5.72295 7.0859 6.31733 7.08381 6.80626 6.81124C8.31881 5.96795 9.25634 4.37192 9.25634 2.64017V0.529297L7.69383 0.529297Z"
                                    fill="#FFD064"
                                ></path>
                                <path
                                    d="M9.26125 11.8117V11.5678C9.26125 11.3292 9.06796 11.1359 8.82945 11.1357H1.83634C1.59773 11.1357 1.4043 11.3292 1.4043 11.5678C1.4043 11.8064 1.59773 11.9998 1.83634 11.9998H9.05901L9.26125 11.8117Z"
                                    fill="#FFD064"
                                ></path>
                                <path
                                    d="M10.1902 11.1357H8.62695C8.86556 11.1357 9.05899 11.3292 9.05899 11.5678V11.9998H10.1902C10.4288 11.9998 10.6223 11.8064 10.6223 11.5678C10.6223 11.3292 10.4288 11.1357 10.1902 11.1357Z"
                                    fill="#FFC250"
                                ></path>
                                <path
                                    d="M8.82572 0.513243V0.168677L8.49088 0L2.48685 0C2.20341 0 1.97363 0.229773 1.97363 0.513219C1.97363 0.796665 2.20341 1.02644 2.48685 1.02644L8.3125 1.02644C8.59592 1.02649 8.82572 0.796689 8.82572 0.513243Z"
                                    fill="#FFD064"
                                ></path>
                                <path
                                    d="M9.54003 0L8.49076 0V0.513219C8.49076 0.796665 8.26096 1.02644 7.97754 1.02644H9.54005C9.8235 1.02644 10.0533 0.796665 10.0533 0.513219C10.0533 0.229773 9.82347 0 9.54003 0V0Z"
                                    fill="#FFC250"
                                ></path>
                                <path
                                    d="M8.28233 10.3667H3.74635C3.64481 10.3667 3.5625 10.2844 3.5625 10.1829C3.5625 10.0813 3.64481 9.99902 3.74635 9.99902H8.28233C8.38388 9.99902 8.46619 10.0813 8.46619 10.1829C8.46619 10.2844 8.38388 10.3667 8.28233 10.3667Z"
                                    fill="#F4FBFF"
                                ></path>
                            </svg>
                        </span>
                        <span className="fund-amount">
                            {formatAmount(tournament.cash_prize || 0)}
                            <span className="currency">FCFA</span>
                        </span>
                    </div>
                    <div className="event-time-discount">
                        {moment(tournament.automation.rounds[0].start_at).diff(moment.now()) > 0 ? 'Débute' : 'Debuté'}{' '}
                        {moment
                            .duration(moment(tournament.automation.rounds[0].start_at).diff(moment.now()))
                            .humanize(true)}
                    </div>
                </div>
            </div>
            <div className="event-data">
                <div className="event-card-info">
                    <h6 className="event-name">{tournament.name}</h6>
                    <div className="game-infos">
                        <div className="info">
                            <span className="icon icon16">
                                <svg
                                    width="16"
                                    height="13"
                                    viewBox="0 0 16 13"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clipPath="url(#clip0_134_1651)">
                                        <path
                                            d="M15.9999 3.11133V9.38825C15.9999 10.8327 12.4871 12.0036 8.15377 12.0036C3.82046 12.0036 0.307617 10.8327 0.307617 9.38825V3.11133"
                                            fill="#FFDB2D"
                                        ></path>
                                        <path
                                            d="M8.15375 3.11133V12.0036C12.4871 12.0036 15.9999 10.8327 15.9999 9.38825V3.11133H8.15375Z"
                                            fill="#FFAF33"
                                        ></path>
                                        <path
                                            d="M8.15377 5.72684C12.4871 5.72684 15.9999 4.5559 15.9999 3.11147C15.9999 1.66704 12.4871 0.496094 8.15377 0.496094C3.82046 0.496094 0.307617 1.66704 0.307617 3.11147C0.307617 4.5559 3.82046 5.72684 8.15377 5.72684Z"
                                            fill="#FFEA8A"
                                        ></path>
                                        <path
                                            d="M8.15375 0.496094V5.72687C12.4871 5.72687 15.9999 4.55593 15.9999 3.1115C15.9999 1.66707 12.4871 0.496094 8.15375 0.496094Z"
                                            fill="#FFDB2D"
                                        ></path>
                                        <path
                                            d="M8.15377 8.0812C3.82046 8.0812 0.307617 6.91025 0.307617 5.46582V7.96896C0.770019 8.27015 1.35998 8.55402 2.10947 8.80391C3.74724 9.3498 5.89377 9.65046 8.15377 9.65046C10.4138 9.65046 12.5603 9.3498 14.1981 8.80391C14.9476 8.55408 15.5376 8.27015 15.9999 7.96896V5.46582C15.9999 6.91025 12.4871 8.0812 8.15377 8.0812Z"
                                            fill="#FFAF33"
                                        ></path>
                                        <path
                                            d="M8.15375 8.0812V9.65043C10.4137 9.65043 12.5603 9.34976 14.198 8.80387C14.9476 8.55405 15.5375 8.27012 15.9999 7.96893V5.46582C15.9999 6.91025 12.4871 8.0812 8.15375 8.0812Z"
                                            fill="#FF9408"
                                        ></path>
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_134_1651">
                                            <rect
                                                width="15.6923"
                                                height="12"
                                                fill="white"
                                                transform="translate(0.307617 0.25)"
                                            ></rect>
                                        </clipPath>
                                    </defs>
                                </svg>
                            </span>
                            <span className="value">
                                {formatAmount(tournament.register_fees)} <span className="currency">XAF</span>
                            </span>
                        </div>
                        <div className="info">
                            <span className="icon icon16">
                                <svg
                                    width="18"
                                    height="14"
                                    viewBox="0 0 18 14"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M5.5 1.75C5.77614 1.75 6 1.52614 6 1.25C6 0.973858 5.77614 0.75 5.5 0.75V1.75ZM5.5 6.11364C5.77614 6.11364 6 5.88978 6 5.61364C6 5.33749 5.77614 5.11364 5.5 5.11364V6.11364ZM4.53415 8.26989C4.79593 8.18199 4.93689 7.89852 4.84899 7.63674C4.7611 7.37497 4.47763 7.23401 4.21585 7.3219L4.53415 8.26989ZM1 12.1102L0.5 12.1095V12.1102H1ZM1 13.2504H0.5C0.5 13.5266 0.723858 13.7504 1 13.7504V13.2504ZM2.74569 13.7504C3.02183 13.7504 3.24569 13.5266 3.24569 13.2504C3.24569 12.9743 3.02183 12.7504 2.74569 12.7504V13.7504ZM13.5 13.25V13.75C13.7761 13.75 14 13.5261 14 13.25H13.5ZM4.5 13.25H4C4 13.5261 4.22386 13.75 4.5 13.75V13.25ZM4.5 12.25H4H4.5ZM9 7.75V7.25V7.75ZM12.5 0.75C12.2239 0.75 12 0.973858 12 1.25C12 1.52614 12.2239 1.75 12.5 1.75V0.75ZM12.5 5.25C12.2239 5.25 12 5.47386 12 5.75C12 6.02614 12.2239 6.25 12.5 6.25V5.25ZM14.2779 7.4731C14.0198 7.37492 13.731 7.50455 13.6328 7.76265C13.5346 8.02075 13.6642 8.30957 13.9223 8.40776L14.2779 7.4731ZM17.0001 12.1404H17.5001V12.1398L17.0001 12.1404ZM17.0001 13.2504V13.7504C17.2762 13.7504 17.5001 13.5266 17.5001 13.2504H17.0001ZM15.5001 12.7504C15.224 12.7504 15.0001 12.9743 15.0001 13.2504C15.0001 13.5266 15.224 13.7504 15.5001 13.7504V12.7504ZM5.5 0.75C4.77553 0.75 4.07776 1.02893 3.56094 1.53009L4.25708 2.24799C4.58417 1.93081 5.03099 1.75 5.5 1.75V0.75ZM3.56094 1.53009C3.04359 2.03176 2.75 2.7155 2.75 3.43182H3.75C3.75 2.99082 3.93052 2.56466 4.25708 2.24799L3.56094 1.53009ZM2.75 3.43182C2.75 4.14813 3.04359 4.83188 3.56094 5.33355L4.25708 4.61565C3.93052 4.29897 3.75 3.87281 3.75 3.43182H2.75ZM3.56094 5.33355C4.07776 5.83471 4.77553 6.11364 5.5 6.11364V5.11364C5.03099 5.11364 4.58417 4.93282 4.25708 4.61565L3.56094 5.33355ZM4.21585 7.3219C3.13844 7.68367 2.20087 8.32893 1.53368 9.18181L2.32131 9.79795C2.85945 9.11004 3.62884 8.57387 4.53415 8.26989L4.21585 7.3219ZM1.53368 9.18181C0.865679 10.0357 0.501578 11.0566 0.500001 12.1095L1.5 12.111C1.50123 11.2914 1.78399 10.4848 2.32131 9.79795L1.53368 9.18181ZM0.5 12.1102V13.2504H1.5V12.1102H0.5ZM1 13.7504H2.74569V12.7504H1V13.7504ZM10.75 3.5C10.75 4.4665 9.9665 5.25 9 5.25V6.25C10.5188 6.25 11.75 5.01878 11.75 3.5H10.75ZM9 5.25C8.0335 5.25 7.25 4.4665 7.25 3.5H6.25C6.25 5.01878 7.48122 6.25 9 6.25V5.25ZM7.25 3.5C7.25 2.5335 8.0335 1.75 9 1.75V0.75C7.48122 0.75 6.25 1.98122 6.25 3.5H7.25ZM9 1.75C9.9665 1.75 10.75 2.5335 10.75 3.5H11.75C11.75 1.98122 10.5188 0.75 9 0.75V1.75ZM13.5 12.75H4.5V13.75H13.5V12.75ZM5 13.25V12.25H4V13.25H5ZM5 12.25C5 11.1891 5.42143 10.1717 6.17157 9.42157L5.46447 8.71447C4.52678 9.65215 4 10.9239 4 12.25H5ZM6.17157 9.42157C6.92172 8.67143 7.93913 8.25 9 8.25V7.25C7.67392 7.25 6.40215 7.77678 5.46447 8.71447L6.17157 9.42157ZM9 8.25C10.0609 8.25 11.0783 8.67143 11.8284 9.42157L12.5355 8.71447C11.5979 7.77678 10.3261 7.25 9 7.25V8.25ZM11.8284 9.42157C12.5786 10.1717 13 11.1891 13 12.25H14C14 10.9239 13.4732 9.65215 12.5355 8.71447L11.8284 9.42157ZM13 12.25V13.25H14V12.25H13ZM12.5 1.75C12.9641 1.75 13.4092 1.93437 13.7374 2.26256L14.4445 1.55546C13.9288 1.03973 13.2293 0.75 12.5 0.75V1.75ZM13.7374 2.26256C14.0656 2.59075 14.25 3.03587 14.25 3.5H15.25C15.25 2.77065 14.9603 2.07118 14.4445 1.55546L13.7374 2.26256ZM14.25 3.5C14.25 3.96413 14.0656 4.40925 13.7374 4.73744L14.4445 5.44454C14.9603 4.92882 15.25 4.22935 15.25 3.5H14.25ZM13.7374 4.73744C13.4092 5.06563 12.9641 5.25 12.5 5.25V6.25C13.2293 6.25 13.9288 5.96027 14.4445 5.44454L13.7374 4.73744ZM13.9223 8.40776C14.6795 8.6958 15.3314 9.20697 15.7917 9.87362L16.6146 9.30543C16.0392 8.47212 15.2244 7.83316 14.2779 7.4731L13.9223 8.40776ZM15.7917 9.87362C16.252 10.5403 16.499 11.331 16.5001 12.1411L17.5001 12.1398C17.4988 11.1271 17.19 10.1387 16.6146 9.30543L15.7917 9.87362ZM16.5001 12.1404V13.2504H17.5001V12.1404H16.5001ZM17.0001 12.7504H15.5001V13.7504H17.0001V12.7504Z"
                                        fill="#E6E6E6"
                                    ></path>
                                </svg>
                            </span>
                            <span className="value">4/4</span>
                        </div>
                        <div className="info">
                            <span className="icon icon16">
                                <svg
                                    width="13"
                                    height="16"
                                    viewBox="0 0 13 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M10 4.25C10 5.17826 9.63125 6.0685 8.97487 6.72487C8.3185 7.38125 7.42826 7.75 6.5 7.75C5.57174 7.75 4.6815 7.38125 4.02513 6.72487C3.36875 6.0685 3 5.17826 3 4.25V1.25H10V4.25Z"
                                        stroke="#E6E6E6"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    ></path>
                                    <path
                                        d="M10 11.25C10 10.3217 9.63125 9.4315 8.97487 8.77513C8.3185 8.11875 7.42826 7.75 6.5 7.75C5.57174 7.75 4.6815 8.11875 4.02513 8.77513C3.36875 9.4315 3 10.3217 3 11.25V14.25H10V11.25Z"
                                        stroke="#E6E6E6"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    ></path>
                                    <path
                                        d="M1 1.25H12"
                                        stroke="#E6E6E6"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    ></path>
                                    <path
                                        d="M1 14.75H12"
                                        stroke="#E6E6E6"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    ></path>
                                </svg>
                            </span>
                            <span className="value">{tournament.game_settings.time_per_move}s</span>
                        </div>
                    </div>
                </div>
                <div className="event-action-section">
                    {!tournament.is_user_registered && (
                        <button
                            type="button"
                            className="event-card-button success"
                            disabled={isJoinning}
                            onClick={handleJoinTournament}
                        >
                            {t('register', {ns: 'actions'})}
                            {!isJoinning && (
                                <span className="icon icon16">
                                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <rect id="view-box" width="24" height="24" fill="none"/>
                                        <path
                                            id="Shape"
                                            d="M5.75,17.5a.75.75,0,0,1,0-1.5h8.8A1.363,1.363,0,0,0,16,14.75v-12A1.363,1.363,0,0,0,14.55,1.5H5.75a.75.75,0,0,1,0-1.5h8.8A2.853,2.853,0,0,1,17.5,2.75v12A2.853,2.853,0,0,1,14.55,17.5ZM7.22,13.28a.75.75,0,0,1,0-1.061L9.939,9.5H.75A.75.75,0,0,1,.75,8H9.94L7.22,5.28A.75.75,0,0,1,8.28,4.22l4,4,.013.013.005.006.007.008.007.008,0,.005.008.009,0,0,.008.01,0,0,.008.011,0,0,.008.011,0,0,.008.011,0,0,.007.011,0,.005.006.01,0,.007,0,.008,0,.009,0,.006.006.011,0,0,.008.015h0a.751.751,0,0,1-.157.878L8.28,13.28a.75.75,0,0,1-1.06,0Z"
                                            transform="translate(3.25 3.25)"
                                            fill="currentColor"
                                        />
                                    </svg>
                                </span>
                            )}
                            {isJoinning && (
                                <span className="flex-none" style={{width: 20}}>
                                    <Spinner color="#FFF"/>
                                </span>
                            )}
                        </button>
                    )}

                    {tournament.is_user_registered && (
                        <button
                            type="button"
                            className="event-card-button danger"
                            disabled={!canLeaveTournament(tournament) || isLeaving}
                            onClick={onLeaveTournament}
                        >
                            {!isLeaving && (
                                <span className="icon icon16">
                                    <span className="icon icon16">
                                        <svg
                                            width="18"
                                            height="18"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M8.25 5.25L9 4.5H18L18.75 5.25V18.75L18 19.5H9L8.25 18.75V16.5H9.75V18H17.25V6H9.75V7.5H8.25V5.25Z"
                                                fill="currentColor"
                                            />
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M7.06068 12.7499L14.25 12.7499L14.25 11.2499L7.06068 11.2499L8.78035 9.53027L7.71969 8.46961L4.18936 11.9999L7.71969 15.5303L8.78035 14.4696L7.06068 12.7499Z"
                                                fill="currentColor"
                                            />
                                        </svg>
                                    </span>
                                </span>
                            )}
                            {t('quit', {ns: 'actions'})}
                            {isLeaving && (
                                <span className="flex-none" style={{width: 20}}>
                                    <Spinner color="#FFF"/>
                                </span>
                            )}
                        </button>
                    )}

                    <button
                        className="event-card-button default"
                        disabled={isShowing}
                        type="button"
                        onClick={_showDetails}
                    >
                        {!isShowing && (
                            <span className="icon icon16">
                                <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <rect width="24" height="24" fill="none"/>
                                    <circle
                                        cx="12"
                                        cy="12"
                                        r="9"
                                        stroke="currentColor"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                    <path
                                        d="M12 11V17"
                                        stroke="currentColor"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                    <path
                                        d="M11.75 8V7H12.25V8H11.75Z"
                                        stroke="currentColor"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </span>
                        )}
                        {isShowing && (
                            <span className="flex-none" style={{width: 20}}>
                                <Spinner color="#FFF"/>
                            </span>
                        )}
                    </button>
                </div>
                <div className="event-suscribers">
                    <div className="suscribers">
                        {subscribers.map((index) => (
                            <div className="user-avatar" key={index}>
                                <img src={getRandomAvatar(index)} alt=""/>
                            </div>
                        ))}
                    </div>
                    {subscribers.length == 0 && <span className="suscribers-count">{t('no_subscription')}</span>}
                    {subscribers.length > 0 && subscribers.length <= tournament.nb_users && (
                        <span className="suscribers-count">
                            {subscribers.length == 1 ? t('1_subscription') : t('full_subscriptions')}
                        </span>
                    )}
                    {tournament.nb_users > 4 && (
                        <span className="suscribers-count">
                            {t('more_subscriptions', {number: tournament.nb_users - 4})}{' '}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TournamentWidget;
